import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Users, Calendar, Building2, Video, Image as ImageIcon, Play } from "lucide-react";

// Utility functions for media type detection
const getFileExtension = (fileName: string) => {
  return fileName.split('.').pop()?.toLowerCase() || '';
};

const isVideoFile = (fileName: string) => {
  const videoExtensions = ['mp4', 'webm', 'ogg', 'avi', 'mov', 'wmv', 'flv', 'm4v'];
  return videoExtensions.includes(getFileExtension(fileName));
};

const isImageFile = (fileName: string) => {
  const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'];
  return imageExtensions.includes(getFileExtension(fileName));
};

export default function MeetTheTeam() {
  const [selectedDepartment, setSelectedDepartment] = useState<string>("");
  const [selectedVideo, setSelectedVideo] = useState<any>(null);
  // Get current user info
  const { data: userInfo, isLoading: userLoading } = useQuery({
    queryKey: ['/auth/me'],
  }) as { data: { user: any } | undefined; isLoading: boolean };

  // Fetch all departments for dropdown
  const { data: departmentsData } = useQuery({
    queryKey: ['/departments'],
  }) as { data: { departments: any[] } | undefined };

  // Get team images for selected department
  const { data: teamImagesData, isLoading: teamImagesLoading } = useQuery({
    queryKey: ['/team-images'],
  }) as { data: { teamImages: any[] } | undefined; isLoading: boolean };

  // Set default department to user's department when user data loads
  useEffect(() => {
    if (userInfo?.user?.departmentId && !selectedDepartment) {
      setSelectedDepartment(userInfo.user.departmentId.toString());
    }
  }, [userInfo?.user?.departmentId, selectedDepartment]);

  // Get department name by ID
  const getDepartmentName = (departmentId: number) => {
    return departmentsData?.departments?.find(dept => dept.id === departmentId)?.name || 'Unknown Department';
  };

  // Filter team images by selected department
  const filteredTeamImages = teamImagesData?.teamImages?.filter(image => 
    selectedDepartment ? image.departmentId.toString() === selectedDepartment : true
  ) || [];

  // Get selected department info
  const currentDepartment = departmentsData?.departments?.find(
    dept => dept.id.toString() === selectedDepartment
  );

  if (userLoading || teamImagesLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-servify-purple/5 via-white to-servify-purple/5 p-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center">
            <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-servify-purple mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading team images...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-servify-purple/5 via-white to-servify-purple/5">
      {/* Header */}
      <div className="text-center space-y-6 pt-6 px-6">
        <div className="flex items-center justify-center space-x-3">
          <Users className="h-8 w-8 text-servify-purple" />
          <h1 className="text-4xl font-bold text-servify-dark-grey">Meet the Team</h1>
        </div>
        <p className="text-xl text-servify-dark-grey/70 max-w-3xl mx-auto">
          Discover the amazing people you'll be working with.
        </p>
        
        {/* Department Selector */}
        <div className="flex items-center justify-center space-x-4 max-w-md mx-auto">
          <span className="text-sm font-medium text-gray-700">Department:</span>
          <Select value={selectedDepartment} onValueChange={setSelectedDepartment}>
            <SelectTrigger className="w-64">
              <SelectValue placeholder="Select Department" />
            </SelectTrigger>
            <SelectContent className="z-[9999] relative bg-white border border-gray-200 shadow-lg" position="popper" sideOffset={4}>
              {departmentsData?.departments?.map((department) => (
                <SelectItem key={department.id} value={department.id.toString()}>
                  {department.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

      </div>

      {/* Team Media Display */}
      {filteredTeamImages && filteredTeamImages.length > 0 ? (
        <div className="space-y-6 p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
            {filteredTeamImages.map((teamImage) => (
              <Card 
                key={teamImage.id} 
                className="group hover:shadow-xl hover:shadow-servify-purple/20 transition-all duration-300 border-2 border-transparent hover:border-servify-purple/20 overflow-hidden"
                data-testid={`team-image-card-${teamImage.id}`}
              >
                {/* Media Section */}
                <div className="relative overflow-hidden">
                  {isVideoFile(teamImage.fileName) ? (
                    <div className="relative w-full h-48 bg-gray-900 flex items-center justify-center group/video">
                      <video
                        src={teamImage.mediaUrl}
                        className="w-full h-full object-cover"
                        controls={false}
                        preload="metadata"
                        onError={(e) => {
                          const video = e.target as HTMLVideoElement;
                          const parent = video.parentElement;
                          if (parent) {
                            parent.innerHTML = `
                              <div class="w-full h-48 bg-gray-100 flex flex-col items-center justify-center">
                                <svg class="w-12 h-12 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
                                </svg>
                                <span class="text-gray-500 text-sm">Video not available</span>
                              </div>
                            `;
                          }
                        }}
                        data-testid={`team-video-${teamImage.id}`}
                      />
                      {/* Video Play Overlay */}
                      <div 
                        className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center opacity-0 group-hover/video:opacity-100 transition-opacity duration-300 cursor-pointer"
                        onClick={() => setSelectedVideo(teamImage)}
                      >
                        <div className="bg-white bg-opacity-90 rounded-full p-3 transform group-hover/video:scale-110 transition-transform duration-300">
                          <Play className="h-8 w-8 text-gray-800 ml-1" />
                        </div>
                      </div>
                      {/* Media Type Badge */}
                      <div className="absolute top-3 left-3">
                        <Badge className="bg-blue-100 text-blue-700 border-blue-200 flex items-center space-x-1 text-xs font-medium">
                          <Video className="h-3 w-3" />
                          <span>Video</span>
                        </Badge>
                      </div>
                    </div>
                  ) : (
                    <div className="relative">
                      <img
                        src={teamImage.mediaUrl}
                        alt={teamImage.title}
                        className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                        onError={(e) => {
                          const img = e.target as HTMLImageElement;
                          img.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjNmNGY2Ii8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzk5YTNhZiIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkltYWdlIG5vdCBhdmFpbGFibGU8L3RleHQ+PC9zdmc+';
                        }}
                        data-testid={`team-image-${teamImage.id}`}
                      />
                      {/* Media Type Badge */}
                      <div className="absolute top-3 left-3">
                        <Badge className="bg-green-100 text-green-700 border-green-200 flex items-center space-x-1 text-xs font-medium">
                          <ImageIcon className="h-3 w-3" />
                          <span>Image</span>
                        </Badge>
                      </div>
                    </div>
                  )}
                </div>

                {/* Content Section */}
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg font-bold text-servify-dark-grey line-clamp-2">
                    {teamImage.title}
                  </CardTitle>
                </CardHeader>

                <CardContent className="pt-0">
                  {teamImage.description && (
                    <p className="text-gray-600 text-sm leading-relaxed mb-4 line-clamp-2">
                      {teamImage.description}
                    </p>
                  )}
                  
                  {/* Metadata */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <div className="flex items-center space-x-1">
                        <Calendar className="h-3 w-3" />
                        <span>{new Date(teamImage.createdAt).toLocaleDateString()}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        {isVideoFile(teamImage.fileName) ? (
                          <>
                            <Video className="h-3 w-3" />
                            <span>Video</span>
                          </>
                        ) : (
                          <>
                            <ImageIcon className="h-3 w-3" />
                            <span>Image</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      ) : (
        /* Empty State */
        <div className="text-center py-16 px-6">
          <div className="space-y-6">
            <div className="mx-auto w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center">
              <Users className="h-12 w-12 text-gray-400" />
            </div>
            <div className="space-y-2">
              <h3 className="text-2xl font-semibold text-gray-700">No Team Media Yet</h3>
              <p className="text-gray-500 max-w-md mx-auto">
                {currentDepartment 
                  ? `No team media has been uploaded for the ${currentDepartment.name} department yet. Check back later or try selecting a different department!`
                  : "Select a department to view team photos and videos."
                }
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Video Modal */}
      {selectedVideo && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-75 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden relative">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <div className="flex items-center space-x-2">
                <Video className="h-5 w-5 text-blue-600" />
                <h3 className="text-lg font-semibold text-gray-900 truncate">{selectedVideo.title}</h3>
              </div>
              <button
                onClick={() => setSelectedVideo(null)}
                className="h-8 w-8 p-0 hover:bg-gray-100 rounded-full flex items-center justify-center"
                data-testid="button-close-video-modal"
              >
                ✕
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-4 max-h-[calc(90vh-80px)] overflow-y-auto">
              <div className="space-y-4">
                {/* Video Container */}
                <div className="relative bg-black rounded-lg overflow-hidden">
                  <video
                    key={selectedVideo.id}
                    src={selectedVideo.mediaUrl}
                    controls
                    className="w-full h-auto max-h-[60vh] object-contain"
                    data-testid={`modal-video-${selectedVideo.id}`}
                  >
                    Your browser does not support the video tag.
                  </video>
                </div>

                {/* Video Information */}
                <div className="space-y-4">
                  {selectedVideo.description && (
                    <div>
                      <h4 className="font-medium text-gray-900 mb-2">Description</h4>
                      <p className="text-gray-600 text-sm leading-relaxed">{selectedVideo.description}</p>
                    </div>
                  )}
                  
                  {/* Metadata Grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4 border-t border-gray-200">
                    <div className="flex items-center space-x-3">
                      <Building2 className="h-4 w-4 text-servify-purple flex-shrink-0" />
                      <div>
                        <span className="text-xs text-gray-500 block">Department</span>
                        <span className="text-sm font-medium text-gray-900">{getDepartmentName(selectedVideo.departmentId)}</span>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-3">
                      <Calendar className="h-4 w-4 text-blue-600 flex-shrink-0" />
                      <div>
                        <span className="text-xs text-gray-500 block">Upload Date</span>
                        <span className="text-sm font-medium text-gray-900">{new Date(selectedVideo.createdAt).toLocaleDateString()}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}